import type { LandingPageLeadPayload } from './landingPageLead';

const MAKE_WEBHOOK_URL =
  'https://hook.us1.make.com/pvoxie9gr2wlwmmohxk0i45tp5bm1vu6';

const ATTRIBUTION_STORAGE_KEY = 'nuborrow:landing-page-attribution';

type TrackingEventName = 'lead_intent' | 'lead_submitted';

interface TrackingAttribution {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  fbclid: string;
}

const EMPTY_ATTRIBUTION: TrackingAttribution = {
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_content: '',
  fbclid: '',
};

function readAttributionFromSearch(search: string): TrackingAttribution {
  const params = new URLSearchParams(search);

  return {
    utm_source: params.get('utm_source') ?? '',
    utm_medium: params.get('utm_medium') ?? '',
    utm_campaign: params.get('utm_campaign') ?? '',
    utm_content: params.get('utm_content') ?? '',
    fbclid: params.get('fbclid') ?? '',
  };
}

function normalizeAttribution(value: unknown): TrackingAttribution {
  if (!value || typeof value !== 'object') {
    return EMPTY_ATTRIBUTION;
  }

  const attribution = value as Partial<TrackingAttribution>;

  return {
    utm_source: typeof attribution.utm_source === 'string' ? attribution.utm_source : '',
    utm_medium: typeof attribution.utm_medium === 'string' ? attribution.utm_medium : '',
    utm_campaign: typeof attribution.utm_campaign === 'string' ? attribution.utm_campaign : '',
    utm_content: typeof attribution.utm_content === 'string' ? attribution.utm_content : '',
    fbclid: typeof attribution.fbclid === 'string' ? attribution.fbclid : '',
  };
}

export function captureLandingPageAttributionFromLocation(search: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const attribution = readAttributionFromSearch(search);

  try {
    window.sessionStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(attribution)
    );
  } catch {}
}

function getTrackingAttribution(): TrackingAttribution {
  if (typeof window === 'undefined') {
    return EMPTY_ATTRIBUTION;
  }

  try {
    const storedValue = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);

    if (storedValue) {
      return normalizeAttribution(JSON.parse(storedValue));
    }
  } catch {}

  const currentAttribution = readAttributionFromSearch(window.location.search);
  captureLandingPageAttributionFromLocation(window.location.search);
  return currentAttribution;
}

function padTwoDigits(value: number): string {
  return String(value).padStart(2, '0');
}

function getTrackingTimestamp(date: Date): { date: string; time: string } {
  return {
    date: `${date.getFullYear()}-${padTwoDigits(date.getMonth() + 1)}-${padTwoDigits(date.getDate())}`,
    time: `${padTwoDigits(date.getHours())}:${padTwoDigits(date.getMinutes())}:${padTwoDigits(date.getSeconds())}`,
  };
}

function splitFullName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  const [firstName, ...rest] = parts;

  return {
    firstName,
    lastName: rest.join(' '),
  };
}

function stringifyTrackingValue(value: string | number | boolean | null | undefined): string {
  if (value === undefined || value === null) {
    return '';
  }

  return typeof value === 'string' ? value : String(value);
}

function buildWebhookPayload(
  payload: LandingPageLeadPayload,
  phoneVerified: string
) {
  const { firstName, lastName } = splitFullName(payload.user_info.name);
  const { date, time } = getTrackingTimestamp(new Date());
  const attribution = getTrackingAttribution();

  return {
    phone_verified: phoneVerified,
    landing_page_url: window.location.href,
    date,
    time,
    first_name: firstName,
    last_name: lastName,
    phone: payload.user_info.phone,
    email: payload.user_info.email,
    house_value: stringifyTrackingValue(payload.form.property_value),
    mortgage_balance: stringifyTrackingValue(
      payload.form.mortgage_balance ?? payload.form.first_mortgage_balance
    ),
    interest_rate: stringifyTrackingValue(payload.form.current_rate),
    renewal_time: stringifyTrackingValue(payload.form.renewal_date),
    need_to_borrow: stringifyTrackingValue(payload.form.requested_amount),
    age: stringifyTrackingValue(payload.form.age),
    total_unsecured_debt: stringifyTrackingValue(payload.form.unsecured_debt),
    avg_interest_on_debt: stringifyTrackingValue(payload.form.debt_rate),
    ...attribution,
  };
}

function pushDataLayerEvent(eventName: TrackingEventName): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName });
}

async function postTrackingWebhook(body: ReturnType<typeof buildWebhookPayload>): Promise<void> {
  const response = await fetch(MAKE_WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  });

  if (!response.ok) {
    throw new Error('Tracking webhook request failed');
  }
}

export function trackLandingPageLeadStage(
  payload: LandingPageLeadPayload,
  eventName: TrackingEventName
): void {
  if (typeof window === 'undefined') {
    return;
  }

  pushDataLayerEvent(eventName);

  const webhookPayload = buildWebhookPayload(
    payload,
    eventName === 'lead_submitted' ? 'Yes' : ''
  );

  void postTrackingWebhook(webhookPayload).catch(() => {});
}